import { Button } from '@avanta/shared/components/ui/Button';
import { SPECIALITY_ICONS } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useFetchSpecialities } from '@avanta/shared/hooks/useFetchSpecialities';
import { useGetSpecialityOptions } from '@avanta/shared/hooks/useGetSpecialityOptions';
import { useHasVerticalOverflow } from '@avanta/shared/hooks/useHasVerticalOverflow';
import { cn } from '@avanta/shared/utils/cn';
import { AnimatePresence } from 'framer-motion';
import { ChevronRight, Mic, User } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatedContainer } from 'components/ui/AnimatedContainer';
import { useSpecialityStepForm } from 'pages/Appointments/AppointmentsReserve/hooks/useSpecialityStepForm';
import { useUserStore } from 'stores/user';
import { AppointmentType } from 'types/appointments';
import { CONTAINER_MD_MAX_WIDTH } from '../../../styles';
import {
  AppointmentsReservePayload,
  type StepProps,
  CONTACT_METHOD_ICONS,
  CONTACT_METHODS,
} from '../../../types';
import { CenterSection } from '../../ui/CenterSection';
import { CheckboxCard } from '../../ui/CheckboxCard';
import { StepSection } from '../../ui/StepSection';
import { SearchPsychologistBanner, SpecialitySkeleton } from './components';

export const SpecialityStep = ({ handleNext }: StepProps): React.ReactElement => {
  const patientId = useUserStore(state => state.user?.id);
  const { data: specialities, isLoading } = useFetchSpecialities(patientId ?? '');
  const centersOptions = useGetSpecialityOptions(AppointmentsReservePayload.backofficeCenterId);
  const citiesOptions = useGetSpecialityOptions(AppointmentsReservePayload.city);
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const {
    specialityBooked,
    contactMethod,
    city,
    backofficeCenterId,
    isDisabled,
    handleOnCityChange,
    handleOnSpecialityChange,
    handleOnContactMethodChange,
  } = useSpecialityStepForm();
  const showSearchPsychologistBanner = !contactMethod && !specialityBooked;
  const scrollRef = useRef<HTMLDivElement>(null);
  const { hasOverflow, isAtBottom } = useHasVerticalOverflow(
    scrollRef,
    'nearestScrollParent',
    null
  );
  const showScrollShadow = hasOverflow && !isAtBottom;

  if (isLoading) {
    return <SpecialitySkeleton />;
  }

  return (
    <AnimatedContainer
      ref={scrollRef}
      className={cn(
        CONTAINER_MD_MAX_WIDTH,
        'flex flex-col gap-4 am-lg:gap-10 px-0 pb-0 relative h-full justify-between am-lg:justify-start'
      )}
    >
      <AnimatePresence>
        {showSearchPsychologistBanner && (
          <div
            className={cn(
              'px-0 am-lg:px-3 w-full sticky am-lg:relative bottom-0 order-2 am-lg:order-1',
              showScrollShadow && 'shadow-mobile-menu-top'
            )}
          >
            <SearchPsychologistBanner />
          </div>
        )}
      </AnimatePresence>
      <div
        className={cn(
          'flex flex-col gap-8 am-lg:gap-10 px-4 order-1 am-lg:order-2',
          !showSearchPsychologistBanner && 'pb-4'
        )}
      >
        <StepSection
          title={t('AppointmentsReserve.Steps.speciality.title')}
          className="grid grid-cols-2 am-lg:grid-cols-4 gap-4"
        >
          {specialities?.map(speciality => (
            <CheckboxCard
              key={speciality.id}
              id={String(speciality.id)}
              label={t(`AppointmentsReserve.Steps.speciality.options.${speciality.name}`)}
              icon={SPECIALITY_ICONS[speciality.id as keyof typeof SPECIALITY_ICONS] ?? User}
              checked={specialityBooked === speciality.id}
              containerClassName="h-20 am-lg:h-24"
              onCheckedChange={() => handleOnSpecialityChange(speciality.id)}
            />
          ))}
        </StepSection>
        <StepSection
          title={t('AppointmentsReserve.Steps.type.title')}
          className="grid grid-cols-2 gap-4"
        >
          {CONTACT_METHODS?.map(method => (
            <CheckboxCard
              key={method}
              id={method}
              label={t(`AppointmentsReserves.Steps.type.options.${method}`)}
              checked={contactMethod === method}
              icon={CONTACT_METHOD_ICONS[method as keyof typeof CONTACT_METHOD_ICONS] ?? Mic}
              onCheckedChange={() => handleOnContactMethodChange(method)}
              containerClassName="h-20 am-lg:h-24"
            />
          ))}
        </StepSection>
        {contactMethod === AppointmentType.InPerson && specialityBooked ? (
          <CenterSection
            citiesOptions={citiesOptions}
            centersOptions={centersOptions}
            city={city}
            backofficeCenterId={backofficeCenterId}
            onCityChange={handleOnCityChange}
          />
        ) : null}
        <div className="flex justify-end">
          <Button
            type="button"
            variant="primary"
            className="w-full am-lg:w-fit"
            size="lg"
            disabled={isDisabled}
            icon="trailing"
            onClick={handleNext}
          >
            {t('AppointmentsReserve.next')}
            <ChevronRight className="size-5" />
          </Button>
        </div>
      </div>
    </AnimatedContainer>
  );
};
